'use client';

import { useContext } from 'react';

import { Project } from '@/app/api';
import { CookiebotContext } from '@/app/components/CookiebotScript/CookiebotScript';
import {
  pushDonationCompletedEvent,
  PushDonationCompletedEventBaseParams,
} from '@/app/hooks/useAnalytics/events/donation/donation-completed';
import { pushInstagramShareOpenEvent } from '@/app/hooks/useAnalytics/events/project-detail/instagram-share-open';
import { pushGtmProjectDetailViewEvent } from '@/app/hooks/useAnalytics/events/project-detail/project-detail-view';
import { pushShareModalOpenEvent } from '@/app/hooks/useAnalytics/events/project-detail/share-modal-open';
import { pushProjectDetailSharedEvent, ShareMethod } from '@/app/hooks/useAnalytics/events/project-detail/shared-event';
import { mixpanelTrack } from '@/app/hooks/useAnalytics/mixpanel-track';
import { MixpanelEvent } from '@/app/hooks/useAnalytics/types';
import { useCurrentUser } from '@/app/hooks/useCurrentUser';
import { useAbGroup } from '@/app/providers/ConfigProvider';

const useAnalytics = () => {
  const { areStatisticsCookiesAllowed } = useContext(CookiebotContext);
  const { currentUser } = useCurrentUser();
  const abGroup = useAbGroup();

  const otherParams = {
    areStatisticsCookiesAllowed,
    abGroup,
    userId: currentUser?.id,
  };

  return {
    donation: {
      pushDonationCompletedEvent: (params: PushDonationCompletedEventBaseParams) =>
        pushDonationCompletedEvent({ ...params, ...otherParams }),
    },
    projectDetail: {
      pushGtmProjectDetailViewEvent: (project: Project) => pushGtmProjectDetailViewEvent({ project, ...otherParams }),
      pushShareModalOpenEvent: () => pushShareModalOpenEvent(otherParams),
      pushInstagramShareOpen: () =>
        pushInstagramShareOpenEvent({ areStatisticsCookiesAllowed, userId: currentUser?.id, abGroup }),
      sharedEvent: (project: Project, shareMethod: ShareMethod) =>
        pushProjectDetailSharedEvent({ project, shareMethod, ...otherParams }),
      printPosterClicked: () =>
        mixpanelTrack(MixpanelEvent.ButtonClicked, {
          type: 'print_share_opened',
          $user_id: currentUser?.id,
          abGroup,
        }),
      supportStoryWidgetClicked: () =>
        mixpanelTrack(MixpanelEvent.ButtonClicked, {
          type: 'detail_cta_bottom',
          $user_id: currentUser?.id,
          abGroup,
        }),
    },
  };
};

export default useAnalytics;
